// src/pages/EditTestPage/index.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, App, Form, Typography, message as antdMessage } from 'antd';
import { db } from '../../firebase-config';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import TestForm from '../TestForm';
import dayjs from 'dayjs';

const { Title } = Typography;

const EditTestPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();

  // AntD v5 の message（存在しない場合は fallback）
  const appCtx = App.useApp?.();
  const message = appCtx?.message ?? antdMessage;

  const [form] = Form.useForm();
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // テストデータ取得
  useEffect(() => {
    const fetchTest = async () => {
      try {
        setLoading(true);
        const ref = doc(db, 'tests', testId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          message.error('テストが見つかりません。');
          // 常に「管理・編集」タブに戻す
          navigate('/tests?tab=manage', { replace: true, state: { activeTab: 'manage' } });
          return;
        }

        const data = snap.data();

        // Form 用に正規化（RangePicker は dayjs）
        const processed = {
          ...data,
          availabilityRange: [
            data?.startTime ? dayjs(data.startTime.toDate()) : null,
            data?.endTime ? dayjs(data.endTime.toDate()) : null,
          ],
          isTimed: !!data?.isTimed,
          isAvailabilitySet: !!(data?.startTime || data?.endTime),
          timeLimit: data?.timeLimit ?? undefined,
        };

        form.setFieldsValue(processed);
        setInitialData(processed);
      } catch (err) {
        console.error('fetchTest error:', err);
        message.error('データの読み込み中にエラーが発生しました。');
        // 常に「管理・編集」タブに戻す
        navigate('/tests?tab=manage', { replace: true, state: { activeTab: 'manage' } });
      } finally {
        setLoading(false);
      }
    };

    fetchTest();
  }, [testId, form, message, navigate]);

  // 更新処理
  const handleUpdate = async (values) => {
    setSaving(true);
    try {
      const {
        availabilityRange,
        testName,
        levelId,
        isTimed,
        timeLimit,
        isAvailabilitySet, // UI 用（保存不要）
        questions,
        totalScore,
        ...rest
      } = values;

      // Firestore に保存するペイロード（undefined は除外）
      const dataToUpdate = {
        ...rest,
        testName: (testName || '').trim(),
        levelId: levelId || null,
        isTimed: !!isTimed,
        timeLimit: isTimed ? Number(timeLimit ?? 0) : null,
        startTime: availabilityRange?.[0] ? availabilityRange[0].toDate() : null,
        endTime: availabilityRange?.[1] ? availabilityRange[1].toDate() : null,
        questions: Array.isArray(questions) ? questions : [],
        totalScore: Number(totalScore ?? 0),
        updatedAt: serverTimestamp(),
      };

      Object.keys(dataToUpdate).forEach((k) => {
        if (dataToUpdate[k] === undefined) delete dataToUpdate[k];
      });

      await updateDoc(doc(db, 'tests', testId), dataToUpdate);
      message.success('更新しました。');

      // 常に「管理・編集」タブに戻る
      navigate('/tests?tab=manage', { replace: true, state: { activeTab: 'manage' } });
    } catch (err) {
      console.error('update error:', err);
      message.error('更新時にエラーが発生しました。');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', marginTop: 50 }} />;
  }

  return (
    <div>
      <Title level={2}>テスト編集</Title>
      {initialData && (
        <TestForm
          form={form}
          onFinish={handleUpdate}
          saving={saving}
          isEditMode={true}
          initialData={initialData}
        />
      )}
    </div>
  );
};

export default EditTestPage;
